import {
  PERF_ARMS_OFF_STORAGE_KEY, PERF_ARMS_STORAGE_KEY, PERF_DRAW_REASONS,
  PERF_EXPERIMENTS, PERF_REPLAY_ARMS, PERF_REPLAY_SALT, PERF_REPLAY_SHARE,
  PERF_STRUCTURAL_ARMS, PERF_TAB_NAME_PREFIX,
} from './perfArms.constants';

export type tPerfExperiment = keyof typeof PERF_EXPERIMENTS;
export type tPerfStructuralArm = typeof PERF_STRUCTURAL_ARMS[number];
export type tPerfReplayArm = typeof PERF_REPLAY_ARMS[number];
export type tPerfDrawReason = typeof PERF_DRAW_REASONS[number];
type tStoredDraw<T extends string> = { value: T; treatmentShare: number };
type tStructuralDraws = Record<tPerfExperiment, tStoredDraw<tPerfStructuralArm>>;
export type tPerfArms = Readonly<{
  tabId: string;
  drawReason: tPerfDrawReason;
  replayArm: tPerfReplayArm;
  drawn: Readonly<Record<tPerfExperiment, tPerfStructuralArm>>;
  effective: Readonly<Record<tPerfExperiment, tPerfStructuralArm>>;
  treatmentShares: Readonly<Record<tPerfExperiment, number>>;
}>;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readDraw = <T extends string>(value: unknown, allowed: readonly T[]): tStoredDraw<T> | undefined => {
  if (!isRecord(value) || !allowed.some((arm) => arm === value.value) ||
      typeof value.treatmentShare !== 'number' || !Number.isFinite(value.treatmentShare) ||
      value.treatmentShare < 0 || value.treatmentShare > 1) return undefined;
  return { value: value.value as T, treatmentShare: value.treatmentShare };
};

const parseStored = (raw: string | null): Record<string, unknown> | undefined => {
  try {
    const value: unknown = raw ? JSON.parse(raw) : undefined;
    return isRecord(value) && value.version === 1 && typeof value.tabId === 'string' &&
      UUID.test(value.tabId) && (value.drawReason === 'new_tab' || value.drawReason === 'auth_reset')
      ? value : undefined;
  } catch {
    return undefined;
  }
};

const drawTreatment = (tabId: string, definition: { salt: string; treatmentShare: number }): boolean => {
  let hash = 2166136261;
  for (const character of `${tabId}:${definition.salt}`) {
    hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
  }
  hash = Math.imul(hash ^ (hash >>> 16), 0x85ebca6b);
  hash = Math.imul(hash ^ (hash >>> 13), 0xc2b2ae35);
  return ((hash ^ (hash >>> 16)) >>> 0) / 4294967296 < definition.treatmentShare;
};

const freezeArms = (arms: tPerfArms): tPerfArms => Object.freeze({
  ...arms,
  drawn: Object.freeze(arms.drawn),
  effective: Object.freeze(arms.effective),
  treatmentShares: Object.freeze(arms.treatmentShares),
});

const isOpenerCopy = (tabId: string): boolean => {
  try {
    const opener: unknown = window.opener;
    return window.name !== `${PERF_TAB_NAME_PREFIX}${tabId}` &&
      isRecord(opener) && opener.name === `${PERF_TAB_NAME_PREFIX}${tabId}`;
  } catch {
    // Cross-origin opener properties are inaccessible.
    return false;
  }
};

const initializeArms = (): tPerfArms => {
  try {
    const storage = window.sessionStorage;
    const raw = storage.getItem(PERF_ARMS_STORAGE_KEY);
    const priorTabId = window.name.startsWith(PERF_TAB_NAME_PREFIX)
      ? window.name.slice(PERF_TAB_NAME_PREFIX.length) : '';
    const parsed = parseStored(raw);
    // window.open copies opener storage; cross-site navigation can clear name.
    const stored = parsed && !isOpenerCopy(String(parsed.tabId)) ? parsed : undefined;
    const tabId = stored ? String(stored.tabId) : crypto.randomUUID();
    const drawReason = stored
      ? stored.drawReason === 'auth_reset' ? 'auth_reset' : 'new_tab'
      : UUID.test(priorTabId) ? 'auth_reset' : 'new_tab';
    const replay = readDraw(stored?.replay, PERF_REPLAY_ARMS) ?? {
      value: drawTreatment(tabId, { salt: PERF_REPLAY_SALT, treatmentShare: PERF_REPLAY_SHARE }) ? 'holdout' : 'recorded',
      treatmentShare: PERF_REPLAY_SHARE,
    };
    const previousStructural = isRecord(stored?.structural) ? stored.structural : {};
    const structural = {} as tStructuralDraws;
    const drawn = {} as Record<tPerfExperiment, tPerfStructuralArm>;
    const effective = {} as Record<tPerfExperiment, tPerfStructuralArm>;
    const treatmentShares = {} as Record<tPerfExperiment, number>;
    const disabled = window.localStorage.getItem(PERF_ARMS_OFF_STORAGE_KEY) === 'true';
    for (const experiment of Object.keys(PERF_EXPERIMENTS) as tPerfExperiment[]) {
      const definition = PERF_EXPERIMENTS[experiment];
      const assignment = readDraw(previousStructural[experiment], PERF_STRUCTURAL_ARMS) ?? {
        value: drawTreatment(tabId, definition) ? 'treatment' : 'control',
        treatmentShare: definition.treatmentShare,
      };
      structural[experiment] = assignment;
      drawn[experiment] = assignment.value;
      effective[experiment] = disabled ? 'control' : assignment.value;
      treatmentShares[experiment] = assignment.treatmentShare;
    }
    const serialized = JSON.stringify({ version: 1, tabId, drawReason, replay, structural });
    if (serialized !== raw) storage.setItem(PERF_ARMS_STORAGE_KEY, serialized);
    window.name = `${PERF_TAB_NAME_PREFIX}${tabId}`;
    return freezeArms({ tabId, drawReason, replayArm: replay.value, drawn, effective, treatmentShares });
  } catch {
    return freezeArms({
      tabId: 'unavailable', drawReason: 'unavailable', replayArm: 'recorded',
      drawn: { perf_s1: 'control', perf_s2: 'control' },
      effective: { perf_s1: 'control', perf_s2: 'control' },
      treatmentShares: { perf_s1: 0, perf_s2: 0 },
    });
  }
};

const arms = initializeArms();

export const getPerfArms = (): tPerfArms => arms;

const armProperties = {
  replay_arm: arms.replayArm,
  perf_tab_id: arms.tabId,
  perf_draw_reason: arms.drawReason,
  ...Object.fromEntries(Object.keys(PERF_EXPERIMENTS).flatMap((key) => {
    const experiment = key as tPerfExperiment;
    return [
      [experiment, arms.effective[experiment]],
      [`${experiment}_drawn`, arms.drawn[experiment]],
      [`${experiment}_treatment_share`, arms.treatmentShares[experiment]],
    ];
  })),
};

export const getPerfArmProperties = (): Record<string, string | number> => ({ ...armProperties });
